import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Select from "react-select";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { FiFilter, FiDownload, FiBarChart2 } from "react-icons/fi";
import * as XLSX from "xlsx";

const SchemeDiversityTable = () => {
  const pid = useSelector((state) => state.user.projectId);
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOrganizations = async () => {
    try {
      const res = await fetch(
        `https://api-project360-insights.haqdarshak.com/kpi/sch-yc-dfl/unique-orgs/?pid=${pid}`
      );
      const result = await res.json();
      const orgs = result.unique_Organizations.map((org) => ({
        label: org,
        value: org,
      }));
      setOrganizationOptions(orgs);
    } catch (error) {
      console.error("Error fetching organizations", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const url = new URL("https://api-project360-insights.haqdarshak.com/kpi/sch-yc-dfl/open/");
      url.searchParams.append("pid", pid);
      if (selectedOrg) {
        url.searchParams.append("OrganizationName", selectedOrg.value);
      }
      const res = await fetch(url.toString());
      const result = await res.json();
      
      // Transform the data into the required table format
      const transformedData = [
        {
          title: "Schemes Without DFL/YC",
          rows: result.filter(item => 
            !item["Schemes ratio"].includes("DFL") && 
            !item["Schemes ratio"].includes("YC")
          )
        },
        {
          title: "Schemes With DFL + YC",
          rows: result.filter(item => 
            item["Schemes ratio"].includes("DFL") && 
            item["Schemes ratio"].includes("YC")
          )
        },
        {
          title: "Schemes With DFL",
          rows: result.filter(item => 
            item["Schemes ratio"].includes("DFL") && 
            !item["Schemes ratio"].includes("YC")
          )
        },
        
        {
          title: "Schemes With YC",
          rows: result.filter(item => 
            !item["Schemes ratio"].includes("DFL") && 
            item["Schemes ratio"].includes("YC")
          )
        }
      ];
      
      setData(transformedData);
    } catch (error) {
      console.error("Error fetching scheme diversity data", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (pid) {
      fetchOrganizations();
    }
  }, [pid]);

  useEffect(() => {
    if (pid) {
      fetchData();
    }
  }, [pid, selectedOrg]);

  const handleDownload = () => {
    // Flatten the data for export
    const flatData = data.flatMap(section => section.rows);
    
    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(flatData);
    
    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "SchemeDiversityData");
    
    // Generate file and download
    XLSX.writeFile(wb, "SchemeDiversityData.xlsx");
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 max-w-[90vw] mx-auto">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="w-full sm:w-64">
            <div className="flex items-center text-md text-gray-700 mb-1">
              <FiFilter className="mr-3" />
              <span>Filter by Organization</span>
            </div>
            <Select
              options={organizationOptions}
              value={selectedOrg}
              onChange={setSelectedOrg}
              isClearable
              placeholder="All organizations"
              className="text-sm text-black"
            />
          </div>
          <button
            onClick={handleDownload}
            className="h-[38px] mt-auto px-4 py-2 bg-green-700 border border-gray-300 rounded-md text-sm font-medium text-white hover:bg-purple-600 hover:text-white flex items-center transition-colors duration-200"
          >
            <FiDownload className="mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Content Section */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : data.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">No data available for the selected filters</p>
        </div>
      ) : (
        <>
          {/* Tables Section in 2x2 Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {data.map((section, sectionIdx) => (
              <div 
                key={sectionIdx} 
                className="overflow-hidden rounded-lg border border-gray-200 shadow-sm"
              >
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-2 border-gray-800">
                          Schemes Ratio
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-2 border-gray-800">
                          No. of Citizens
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {section.rows.map((item, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 border-2 border-gray-800">
                            {item["Schemes ratio"]}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 border-2 border-gray-800">
                            {item["No. of Citizens"]}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>

        </>
      )}
    </div>
  );
};

export default SchemeDiversityTable;